import { css, cva } from '@lastbrand/web-ui-styled-system/css'

import { LinkPure } from '@src/quince-components/src/atoms/link'

import type { SystemStyleObject } from '@lastbrand/web-ui-styled-system/types'
import type { KeyboardEvent, ReactNode, Ref } from 'react'

// Exported so the account controls can mirror the category-row hover effect exactly.
export const navLabelUnderlineReveal = {
  _hover: { '& [data-nav-label]::after': { opacity: 1 } },
  _active: { '& [data-nav-label]::after': { opacity: 1 } },
  // Hold the underline for as long as this item's flyout is open. Reaching the panel means the
  // pointer has left the trigger, so `:hover` stops matching and the underline used to vanish while
  // the menu was still open — legacy keeps it lit (STRFRNT-16122). `aria-expanded` is already the
  // open flag on both the category triggers and the account control, so no new prop is needed.
  '&[aria-expanded="true"] [data-nav-label]::after': { opacity: 1 },
} satisfies SystemStyleObject

const navItemRecipe = cva({
  base: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'space-4',
    flexShrink: 0,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    background: 'transparent',
    textAlign: 'center',
    transition: 'color 150ms ease-out',
    ...navLabelUnderlineReveal,
    // Ring on the label, not the item: the item is as tall as the 64px row, so an outset
    // ring on it always pokes past the band and gets covered by the flyout panel below.
    _focusVisible: {
      outline: 'none',
      '& [data-nav-label]': {
        outline: '2px solid',
        outlineColor: 'black',
        outlineOffset: '0.25rem',
        borderRadius: 'radius-outline',
      },
    },
  },
  variants: {
    variant: {
      default: {
        color: 'black',
        _active: { color: 'mediumgray' },
      },
      cantaloupe: {
        color: 'cantaloupe-dark-100',
        _active: { color: 'cantaloupe-dark-80' },
      },
    },
    size: {
      sm: { textStyle: 'body-12-strong', height: '40px' },
      md: { textStyle: 'body-14-strong', height: { base: '56px', lg: '64px' } },
    },
  },
  defaultVariants: {
    variant: 'default',
    size: 'md',
  },
})

export const navLabelClass = css({
  position: 'relative',
  display: 'inline-flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  _after: {
    content: '""',
    position: 'absolute',
    insetInline: 0,
    bottom: 0,
    height: '1px',
    backgroundColor: 'currentColor',
    opacity: 0,
    transition: 'opacity 150ms ease-out',
  },
})

interface NavItemProps {
  children: ReactNode
  href?: string
  variant?: 'default' | 'cantaloupe'
  size?: 'sm' | 'md'
  icon?: ReactNode
  css?: SystemStyleObject
  triggerRef?: Ref<HTMLElement>
  /** Identifier passed back to `onPointerEnter` / `onFocus` so a parent can use a single stable handler. */
  itemId?: string
  /** Fires only on the navigating-link variant (when `href` is set). */
  onClick?: () => void
  onPointerEnter?: (id: string) => void
  onPointerLeave?: () => void
  onFocus?: (id: string) => void
  onBlur?: () => void
  onKeyDown?: (id: string, event: KeyboardEvent<HTMLElement>) => void
  'aria-expanded'?: boolean
  'aria-controls'?: string
  'data-testid'?: string
}

export const NavItem = ({
  children,
  href,
  variant = 'default',
  size = 'md',
  icon,
  css: cssProp,
  triggerRef,
  itemId,
  onClick,
  onPointerEnter,
  onPointerLeave,
  onFocus,
  onBlur,
  onKeyDown,
  'aria-expanded': ariaExpanded,
  'aria-controls': ariaControls,
  'data-testid': dataTestId,
}: NavItemProps) => {
  const handlePointerEnter =
    onPointerEnter && itemId !== undefined
      ? () => {
          onPointerEnter(itemId)
        }
      : undefined
  const handleFocus =
    onFocus && itemId !== undefined
      ? () => {
          onFocus(itemId)
        }
      : undefined
  const handleKeyDown =
    onKeyDown && itemId !== undefined
      ? (event: KeyboardEvent<HTMLElement>) => {
          onKeyDown(itemId, event)
        }
      : undefined
  const content = (
    <>
      {icon && (
        <span
          className={css({
            display: 'inline-flex',
            flexShrink: 0,
            width: '24px',
            height: '24px',
          })}
        >
          {icon}
        </span>
      )}
      <span data-nav-label className={navLabelClass}>
        {children}
      </span>
    </>
  )

  if (href !== undefined) {
    return (
      <LinkPure
        ref={triggerRef as Ref<HTMLAnchorElement> | undefined}
        className={css(navItemRecipe.raw({ variant, size }), cssProp)}
        href={href}
        onClick={onClick}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={onPointerLeave}
        onFocus={handleFocus}
        onBlur={onBlur}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        data-testid={dataTestId}
      >
        {content}
      </LinkPure>
    )
  }

  if (ariaExpanded !== undefined) {
    return (
      <button
        type="button"
        ref={triggerRef as Ref<HTMLButtonElement> | undefined}
        className={css(navItemRecipe.raw({ variant, size }), cssProp)}
        onPointerEnter={handlePointerEnter}
        onPointerLeave={onPointerLeave}
        onFocus={handleFocus}
        onBlur={onBlur}
        onKeyDown={handleKeyDown}
        aria-expanded={ariaExpanded}
        aria-controls={ariaControls}
        data-testid={dataTestId}
      >
        {content}
      </button>
    )
  }

  return (
    <div
      className={css(navItemRecipe.raw({ variant, size }), cssProp)}
      onPointerEnter={handlePointerEnter}
      onPointerLeave={onPointerLeave}
      onFocus={handleFocus}
      onBlur={onBlur}
      data-testid={dataTestId}
    >
      {content}
    </div>
  )
}
